import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as api from "../../api/hub/project-agents";
import { listProjects } from "../../api/hub/work";
export const agentStatus: Record<api.AgentStatus, string> = {
  active: "监测中",
  waiting: "等待后续",
  paused: "已暂停",
  needs_decision: "待人工决定",
  completed: "已完成",
  error: "运行出错",
  budget_exhausted: "已达预算上限",
};
export function useProjectAgents(id: string, projectId?: string, stepsOffset = 0) {
  const qc = useQueryClient();
  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ["hub", "project-agents"] });
  };
  return {
    list: useQuery({
      queryKey: ["hub", "project-agents", "list", projectId],
      queryFn: () => api.listProjectAgents(projectId),
      refetchInterval: 15000,
    }),
    detail: useQuery({
      queryKey: ["hub", "project-agents", "detail", id, stepsOffset],
      queryFn: () => api.getProjectAgent(id, stepsOffset),
      enabled: !!id,
      refetchInterval: 15000,
    }),
    deliveryReviews: useQuery({
      queryKey: ["hub", "project-agents", "delivery-reviews", id],
      queryFn: () => api.listAgentDeliveryReviews(id),
      enabled: !!id,
      refetchInterval: 15000,
    }),
    customerReviews: useQuery({
      queryKey: ["hub", "project-agents", "customer-reviews", id],
      queryFn: () => api.listAgentCustomerReviews(id),
      enabled: !!id,
      refetchInterval: 15000,
    }),
    projects: useQuery({
      queryKey: ["hub", "projects"],
      queryFn: listProjects,
    }),
    create: useMutation({
      mutationFn: api.createProjectAgent,
      onSuccess: refresh,
    }),
    update: useMutation({
      mutationFn: ({
        id,
        patch,
      }: {
        id: string;
        patch: api.ResponsibilityPatch;
      }) => api.updateProjectAgent(id, patch),
      onSuccess: refresh,
    }),
    change: useMutation({
      mutationFn: ({
        id,
        action,
      }: {
        id: string;
        action: "pause" | "resume" | "run";
      }) => api.changeProjectAgent(id, action),
      onSuccess: refresh,
    }),
    source: useMutation({
      mutationFn: ({ id, source }: { id: string; source: api.SourceInput }) =>
        api.addAgentSource(id, source),
      onSuccess: refresh,
    }),
    decision: useMutation({
      mutationFn: ({
        id,
        decisionId,
        answer, version, optionId,
      }: {
        id: string;
        decisionId: string;
        answer: string;
        version: number;
        optionId: string;
      }) => api.resolveAgentDecision(id, decisionId, answer, version, optionId),
      onSuccess: refresh,
    }),
    confirm: useMutation({
      mutationFn: ({
        id,
        criterionId,
        evidence,
      }: {
        id: string;
        criterionId: string;
        evidence: string;
      }) => api.confirmAgentCriterion(id, criterionId, evidence),
      onSuccess: refresh,
    }),
    verifyDelivery: useMutation({
      mutationFn: ({ id, review, messageId }: {
        id: string;
        review: Pick<api.AgentDeliveryReview, "id" | "kind">;
        messageId: string;
      }) => api.verifyAgentDelivery(id, review, messageId),
      onSuccess: refresh,
    }),
    decideCustomerReview: useMutation({
      mutationFn: ({ id, reviewId, decision, edited }: {
        id: string;
        reviewId: string;
        decision: "publish" | "reject" | "reconcile";
        edited: Record<string, string>;
      }) => api.decideAgentCustomerReview(id, reviewId, decision, edited),
      onSuccess: refresh,
    }),
  };
}
